"use client"

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavbarEffects } from '@/hooks/useNavbarEffects'

type Props = {
  lightSrc: string
  darkSrc: string
  scrolled: boolean
  href?: string
  scale?: number
}

export default function AnniversaryLogo({ lightSrc, darkSrc, scrolled, href = '/', scale = 1 }: Props) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  // Load effects configuration from admin (for snow, lottie, fireworks ONLY)
  const { config: effectsConfig } = useNavbarEffects()

  // Always use props for logos - this avoids Supabase data issues
  // Logo control from admin is disabled until Supabase data is fixed
  const src = scrolled ? darkSrc : lightSrc

  const showSnow = effectsConfig.active && effectsConfig.snowEnabled
  const showLottie = effectsConfig.active && effectsConfig.lottieEnabled
  const showFireworks = effectsConfig.active && effectsConfig.fireworksEnabled

  // Selection based on scrolled state
  const lottieUrl = scrolled ? effectsConfig.lottieScrolledUrl : effectsConfig.lottieTransparentUrl

  type Dot = { x: number; y: number; c: string; d: string; w: number; h: number; dur: string; rot: number; jx: number; jy: number }
  const brandRed = '#fb0009'
  // Seed cycles to re-randomize positions/timing; will tick while hovering
  const [seed, setSeed] = useState(0)
  const [hovering, setHovering] = useState(false)
  // Show hover-styles for the first 5 seconds after mount even without cursor
  const [initialActive, setInitialActive] = useState(false)
  const isHoverActive = hovering || initialActive
  const cycleRef = useRef<number | null>(null)
  // Rocket ascent timing before explosion (slower, slightly variable)
  const rocketDur = useMemo(() => +(0.95 + Math.random() * 0.55).toFixed(2), [seed])
  const rocketRx = useMemo(() => Math.round((Math.random() - 0.5) * 16), [seed]) // slight sideways path
  // Random explosion center offset so it doesn't always explode in the same spot
  const cx = useMemo(() => Math.round((Math.random() - 0.5) * 88), [seed]) // -44..44 px horizontally
  const cy = useMemo(() => Math.round((Math.random() - 0.5) * 20), [seed]) // -10..10 px vertically
  // Palette adapts for contrast: on white navbar avoid white; on black navbar avoid black.
  const palette = (['#ffffff', '#9ca3af', '#000000', brandRed] as const).map(c => {
    if (scrolled && c === '#ffffff') return brandRed
    if (!scrolled && c === '#000000') return brandRed
    return c
  }) as string[]
  // Fireworks bursts (smaller, timed)
  // Start bursts after rocket ascent so it rises then explodes
  const bursts = useMemo(() => ([
    { base: rocketDur + 0.00, count: 18 },
    { base: rocketDur + 1.10, count: 20 },
    { base: rocketDur + 2.20, count: 22 },
  ] as const), [rocketDur])
  // Random center per burst so explosions don't share a single point
  const burstCenters = useMemo(() => (
    bursts.map(() => ({
      cx: Math.round((Math.random() - 0.5) * 88), // -44..44px
      cy: Math.round((Math.random() - 0.5) * 22), // -11..11px
    }))
  ), [bursts, seed])
  // Tiny circular sparks (1–2px)
  const sizes = [
    { w: 1, h: 1 },
    { w: 2, h: 2 },
  ]
  // Build particles per burst so they explode together
  const dots: Dot[] = useMemo(() => bursts.flatMap((b, bi) => {
    const arr: Dot[] = []
    const baseR = 34
    for (let i = 0; i < b.count; i++) {
      const theta = Math.random() * Math.PI * 2
      const radius = baseR + (Math.random() * 16 - 8) // randomize radius
      const x = Math.round(Math.cos(theta) * radius)
      const y = Math.round(Math.sin(theta) * radius)
      const c = palette[(i + bi) % palette.length]
      const d = (b.base + i * 0.02).toFixed(2) + 's' // slightly slower staggering
      const dur = (0.9 + Math.random() * 0.7).toFixed(2) + 's' // longer spark lifetime
      const sz = sizes[i % sizes.length]
      const rot = Math.floor(Math.random() * 360)
      const jx = Math.round((Math.random() - 0.5) * 6)
      const jy = Math.round((Math.random() - 0.5) * 6)
      arr.push({ x, y, c, d, w: sz.w, h: sz.h, dur, rot, jx, jy })
    }
    return arr
  }), [bursts, palette, sizes, seed])

  // Snow flakes for seasonal effect (generated once so animations don't get cut on rerender)
  const snowFlakesRef = useRef<Array<{ left: number; size: number; dur: string; delay: string; opacity: number; sway: number }> | undefined>(undefined)
  // Generate snow flakes based on density from config
  const snowDensity = effectsConfig.snowDensity || 48
  if (!snowFlakesRef.current || snowFlakesRef.current.length !== snowDensity) {
    snowFlakesRef.current = Array.from({ length: snowDensity }).map(() => {
      const left = Math.round(Math.random() * 100)
      const size = Math.round(2 + Math.random() * 6) // 2..8px
      const dur = (3 + Math.random() * 4).toFixed(2) + 's' // fall duration (faster: ~3–7s)
      const delay = (Math.random() * 3).toFixed(2) + 's'
      const opacity = +(0.6 + Math.random() * 0.4).toFixed(2)
      const sway = +(6 + Math.random() * 8).toFixed(2) // sway distance in px
      return { left, size, dur, delay, opacity, sway }
    })
  }
  const snowFlakes = snowFlakesRef.current

  // While hovered (or during initialActive), continuously cycle seed to keep positions random
  useEffect(() => {
    if (!isHoverActive) {
      if (cycleRef.current) {
        window.clearInterval(cycleRef.current)
        cycleRef.current = null
      }
      return
    }
    // Kick off immediately then repeat
    setSeed(s => s + 1)
    cycleRef.current = window.setInterval(() => setSeed(s => s + 1), 3600)
    return () => {
      if (cycleRef.current) {
        window.clearInterval(cycleRef.current)
        cycleRef.current = null
      }
    }
  }, [isHoverActive])
  const lottieContainerRef = useRef<HTMLDivElement | null>(null)
  const lottieElemRef = useRef<any>(null)

  // Dynamically load Lottie web component (if not present) and mount a player into the container.
  useEffect(() => {
    if (!mounted) return
    const container = lottieContainerRef.current
    if (!container) return

    let cancelled = false
    const ensureScript = async () => {
      // If web component already defined, skip loading script
      if (!(window as any).customElements || (window as any).customElements.get('lottie-player')) return
      return new Promise<void>((resolve, reject) => {
        const s = document.createElement('script')
        s.src = 'https://unpkg.com/@lottiefiles/lottie-player@latest/dist/lottie-player.js'
        s.async = true
        s.onload = () => resolve()
        s.onerror = () => reject(new Error('failed to load lottie-player script'))
        document.head.appendChild(s)
      })
    }

    ensureScript().then(() => {
      if (cancelled) return
      // remove previous element if any
      if (lottieElemRef.current) {
        try { lottieElemRef.current.remove() } catch (e) { }
        lottieElemRef.current = null
      }
      // Create player element via DOM to avoid TSX intrinsic element typing
      const el = document.createElement('lottie-player') as any
      // Select JSON based on scrolled state; adjust paths to your public/animations/*.json files
      const playerSrc = scrolled ? effectsConfig.lottieScrolledUrl : effectsConfig.lottieTransparentUrl
      el.setAttribute('src', playerSrc || (scrolled ? '/animations/renos-black.json' : '/animations/renos-white.json'))
      el.setAttribute('background', 'transparent')
      el.setAttribute('speed', '1')
      el.setAttribute('loop', '')
      el.setAttribute('autoplay', '')
      el.style.width = '100%'
      el.style.height = '100%'
      el.style.display = 'block'
      lottieElemRef.current = el
      container.appendChild(el)
    }).catch(() => {
      // If script failed to load, leave fallback (logo img remains visible under hover styles if needed)
    })

    return () => {
      cancelled = true
      if (lottieElemRef.current) {
        try { lottieElemRef.current.remove() } catch (e) { }
        lottieElemRef.current = null
      }
    }
  }, [mounted, scrolled])
  // Activate initial hover for 5 seconds after mount
  useEffect(() => {
    setInitialActive(true)
    const t = window.setTimeout(() => setInitialActive(false), 5000)
    return () => window.clearTimeout(t)
  }, [])
  return (
    <div
      className="relative group"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Seasonal background: falling snow (controlled from admin) */}
      {mounted && showSnow && (
        <>
          <div className="pointer-events-none overflow-hidden" style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 'var(--navbar-height,64px)', zIndex: -10 }}>
            {snowFlakes.map((f, i) => (
              <span
                key={`snow-${i}`}
                className="snow-flake"
                style={{
                  left: `${f.left}%`,
                  // use CSS var for size so pseudo elements can reference it
                  ['--snow-size' as any]: `${f.size}px`,
                  opacity: f.opacity,
                  // Use CSS variables so updating JS won't force a remount/restart
                  ['--snow-dur' as any]: f.dur,
                  ['--snow-delay' as any]: f.delay,
                  ['--snow-sway' as any]: `${f.sway}px`,
                  ['--snow-color' as any]: scrolled ? brandRed : 'rgba(255,255,255,0.95)',
                } as React.CSSProperties as any}
              />
            ))}
          </div>
        </>
      )}

      {/* Fireworks effect (controlled from admin) */}
      {mounted && showFireworks && (
        <div key={seed} className="absolute inset-0 -z-10 pointer-events-none overflow-visible">
          <div className="relative h-full w-full">
            {/* Rocket ascent */}
            <span
              className="fw-rocket"
              style={{
                left: `calc(50% + ${burstCenters[0]?.cx ?? 0}px)`,
                top: `calc(50% + ${burstCenters[0]?.cy ?? 0}px)`,
                width: 2,
                height: 2,
                marginLeft: '-1px',
                marginTop: '-1px',
                color: brandRed,
                background: 'currentColor',
                // @ts-ignore custom properties for keyframes
                '--rx': `${rocketRx}px`,
                animationDuration: `${rocketDur}s`,
              } as React.CSSProperties as any}
            />
            {bursts.map((b, bi) => (
              <React.Fragment key={`b-${bi}`}>
                {dots.slice(
                  bursts.slice(0, bi).reduce((acc, prev) => acc + prev.count, 0),
                  bursts.slice(0, bi + 1).reduce((acc, prev) => acc + prev.count, 0)
                ).map((d, i) => (
                  <span
                    key={`d-${bi}-${i}`}
                    className="fw-dot"
                    style={{
                      left: `calc(50% + ${burstCenters[bi]?.cx ?? 0}px)`,
                      top: `calc(50% + ${burstCenters[bi]?.cy ?? 0}px)`,
                      width: d.w,
                      height: d.h,
                      marginLeft: `${-d.w / 2}px`,
                      marginTop: `${-d.h / 2}px`,
                      background: 'currentColor',
                      color: d.c,
                      borderRadius: '9999px',
                      // @ts-ignore - custom properties for keyframes
                      '--tx': `${d.x}px`,
                      '--ty': `${d.y}px`,
                      '--jx': `${d.jx}px`,
                      '--jy': `${d.jy}px`,
                      '--rot': `${d.rot}deg`,
                      animationDelay: d.d,
                      animationDuration: d.dur,
                    } as React.CSSProperties as any}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Logo with hover swap to Lottie animation (controlled from admin) */}
      <div className="relative w-full" style={{ transform: `scale(${scale})`, transformOrigin: 'left center' }}>
        <img
          src={src}
          alt="LET Logo"
          width={160}
          height={74}
          className={`w-full h-auto object-contain transition-opacity duration-300 ${showLottie && isHoverActive ? 'opacity-0' : 'opacity-100'}`}
        />
        {showLottie && lottieUrl && (
          <iframe
            src={lottieUrl}
            title="Animación de aniversario"
            width={160}
            height={74}
            aria-hidden
            loading="lazy"
            style={{ border: 0, display: 'block' }}
            className={`absolute inset-0 m-auto w-full h-auto object-contain transition-opacity duration-300 ${isHoverActive ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        <a href={href} className="absolute inset-0" aria-label="Ir al inicio" />
      </div>

      <style jsx>{`
        .fw-rocket {
          position: absolute;
          border-radius: 9999px;
          animation-name: none; /* idle until hover */
          animation-timing-function: cubic-bezier(.3,.8,.25,1);
          animation-iteration-count: 1;
          box-shadow: 0 0 8px color-mix(in srgb, currentColor 65%, transparent),
                      0 0 3px color-mix(in srgb, currentColor 85%, transparent);
        }
        .fw-rocket::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          width: 2px;
          height: 26px;
          transform: translate(-50%, 0);
          background: linear-gradient(to bottom, color-mix(in srgb, currentColor 85%, white) 0%, transparent 70%);
          filter: blur(0.6px);
          opacity: .0;
        }
        /* Activate rocket on hover */
        .group:hover .fw-rocket {
          animation-name: rocket-rise;
        }
        .group:hover .fw-rocket::after { opacity: .9; }
        @keyframes rocket-rise {
          0%   { transform: translate(var(--rx), 34px) scale(.9); opacity: .15; }
          40%  { opacity: .9; }
          85%  { transform: translate(calc(var(--rx) * .25), 4px) scale(1); opacity: 1; }
          100% { transform: translate(0, 0) scale(1); opacity: 0; }
        }
        .fw-dot {
          position: absolute;
          animation-name: none; /* idle by default */
          animation-timing-function: cubic-bezier(.2,.7,.2,1);
          animation-iteration-count: 1;
          opacity: 0;
          will-change: transform, opacity, filter;
          box-shadow: 0 0 10px color-mix(in srgb, currentColor 65%, transparent),
                      0 0 4px color-mix(in srgb, currentColor 85%, transparent);
        }
        .fw-dot::after {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 9999px;
          background: radial-gradient(circle, color-mix(in srgb, currentColor 80%, white) 0%, transparent 60%);
          opacity: .0;
          transition: opacity .15s ease-out;
        }
        /* Run sparks only on logo hover */
        .group:hover .fw-dot { animation-name: spark-pop; }
        .group:hover .fw-dot::after { opacity: .45; }
        @keyframes spark-pop {
          0%   { transform: translate(0,0) scale(.6); opacity: 0; filter: brightness(1.2); }
          8%   { opacity: 1; }
          30%  { transform: translate(calc(var(--tx)*0.55 + var(--jx)), calc(var(--ty)*0.55 + var(--jy))) scale(.9); opacity: .95; filter: brightness(1.35); }
          45%  { filter: brightness(1.0); }
          55%  { filter: brightness(1.25); }
          70%  { transform: translate(calc(var(--tx)*0.9 + var(--jx)), calc(var(--ty)*0.9 + var(--jy))) scale(1); opacity: .7; }
          100% { transform: translate(calc(var(--tx) + var(--jx)), calc(var(--ty) + var(--jy))) scale(1); opacity: 0; filter: brightness(.85) blur(.6px); }
        }
        /* Snow flakes */
        .snow-flake {
          position: absolute;
          top: 0;
          width: var(--snow-size, 6px);
          height: var(--snow-size, 6px);
          border-radius: 20%;
          background: radial-gradient(circle at 40% 35%, var(--snow-color, #ffffff) 0%, var(--snow-color, #ffffff) 70%, color-mix(in srgb, var(--snow-color, #ffffff) 85%, transparent) 90%);
          filter: drop-shadow(0 1px 0 rgba(0,0,0,0.06));
          will-change: top, transform, opacity;
          /* durations and delays come from CSS variables set inline: --snow-dur, --snow-delay */
          animation: snow-fall var(--snow-dur, 10s) linear var(--snow-delay, 0s) infinite;
        }
        .snow-flake::before,
        .snow-flake::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          background: linear-gradient(90deg, color-mix(in srgb, var(--snow-color, #ffffff) 98%, white) 0%, color-mix(in srgb, var(--snow-color, #ffffff) 90%, white) 100%);
          border-radius: 2px;
        }
        .snow-flake::before {
          width: calc(var(--snow-size, 6px) * 1.6);
          height: 2px;
        }
        .snow-flake::after {
          width: 2px;
          height: calc(var(--snow-size, 6px) * 1.6);
        }
        /* add a gentle sway using transform with a secondary animation that only affects translateX */
        @keyframes snow-sway-x {
          0% { transform: translateX(0); }
          50% { transform: translateX(calc(var(--snow-sway, 12px) * -1)); }
          100% { transform: translateX(var(--snow-sway, 12px)); }
        }
        @keyframes snow-fall {
          0% { top: -20%; opacity: 0.95; }
          8% { opacity: 1; }
          100% { top: 120%; opacity: 0.95; }
        }
        /* Attach the sway animation to a transform so it won't interfere with top animation.
           Use animation on pseudo-element to render sway visually via before/after? Instead, apply
           a slight CSS transition on transform via inline style using animation shorthand on the element
           but ensure it uses only translateX. We'll apply it below using additional class when needed. */
        .snow-flake { animation-name: snow-fall, snow-sway-x; animation-duration: var(--snow-dur, 10s), calc(var(--snow-dur, 10s) / 6); animation-timing-function: linear, ease-in-out; animation-delay: var(--snow-delay, 0s), 0s; animation-iteration-count: infinite, infinite; animation-direction: normal, alternate; }
  @media (prefers-reduced-motion: reduce) { .fw-dot, .fw-rocket { animation: none !important; display: none; } }
        @media (prefers-reduced-motion: reduce) { .snow-flake { animation: none !important; display: none; } }
      `}</style>
    </div>
  )
}
